import logoEspaceClient from '../images/Logo-Espace-client.png'
import { useDarkMode } from '../hooks/useDarkMode'
import { useModules } from '../hooks/useModules'
import { useState, useEffect, useRef, useCallback, KeyboardEvent } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import {
  LayoutDashboard,
  FileText,
  ShoppingCart,
  Truck,
  Headphones,
  LogOut,
  Menu,
  X,
  User,
  Package,
  FileSignature,
  Shield,
  Server,
  Building2,
  Users,
  ScrollText,
  FolderKanban,
  UserPlus,
  LayoutGrid,
  UsersRound,
  Search,
  UserCheck,
  Moon,
  Sun,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  Clock,
  BarChart2,
  CreditCard,
  CalendarCheck,
  Wallet,
  ArrowLeftRight,
  HelpCircle,
  Settings,
  BellRing,
  Info,
  RefreshCw,
  CheckCircle2,
  XCircle,
  LucideIcon,
} from 'lucide-react'
import api from '../services/api'
import type { AppNotification } from '../types'

interface ClientResult {
  id: number
  customer_name: string
  erp_customer_id: string
  city: string | null
  erp_type: string
  erp_name: string
}

interface ERPOption {
  id: number
  erp_name: string
}

type NavLinkItem = {
  name: string
  href: string
  icon: LucideIcon
  superAdminOnly: boolean
  erpAdminOnly?: boolean
}
type NavSectionItem = { sectionLabel: string; superAdminOnly: boolean }
type AdminNavItem = NavLinkItem | NavSectionItem

// Mapping module → navigation items (order defines the sidebar display order)
const MODULE_NAV_MAP: Record<string, { name: string; href: string; icon: LucideIcon }> = {
  catalog: { name: 'Catalogue', href: '/catalog', icon: Package },
  proposals: { name: 'Devis', href: '/proposals', icon: FileSignature },
  orders: { name: 'Commandes', href: '/orders', icon: ShoppingCart },
  invoices: { name: 'Factures', href: '/invoices', icon: FileText },
  projects: { name: 'Projets', href: '/projects', icon: FolderKanban },
  tickets: { name: 'SAV', href: '/tickets', icon: Headphones },
  gallery: { name: 'Documents', href: '/gallery', icon: LayoutGrid },
  shipments: { name: 'Livraisons', href: '/shipments', icon: Truck },
  document_signatures: { name: 'Signatures', href: '/document-signatures', icon: FileSignature },
}

export default function Layout() {
  const { t } = useTranslation()
  const { isDark, toggle: toggleDarkMode } = useDarkMode()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebar_collapsed') === 'true'
  )

  const toggleCollapsed = () => {
    setSidebarCollapsed((prev) => {
      localStorage.setItem('sidebar_collapsed', String(!prev))
      return !prev
    })
  }
  const { enabledModules, setEnabledModules } = useModules()
  const [customerLogoUrl, setCustomerLogoUrl] = useState<string | null>(null)
  const [supplierWebsite, setSupplierWebsite] = useState<string | null>(null)

  // Client switcher state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<ClientResult[]>([])
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [impersonatedClient, setImpersonatedClient] = useState<ClientResult | null>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ERP admin impersonation state (super_admin only)
  const [erpList, setErpList] = useState<ERPOption[]>([])
  const [erpSelectOpen, setErpSelectOpen] = useState(false)
  const erpSelectRef = useRef<HTMLDivElement>(null)

  // Notification state
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifHelpOpen, setNotifHelpOpen] = useState(false)
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [notifOffset, setNotifOffset] = useState(0)
  const [notifHasMore, setNotifHasMore] = useState(true)
  const [notifLoadingMore, setNotifLoadingMore] = useState(false)
  const notifRef = useRef<HTMLDivElement>(null)
  const notifListRef = useRef<HTMLDivElement>(null)
  const NOTIF_PAGE = 10
  const NOTIF_MAX = 100

  const location = useLocation()
  const navigate = useNavigate()
  const user = api.getUser()

  const isSuperAdmin = user?.role === 'super_admin'
  const isAnyAdmin = user?.role === 'admin' || isSuperAdmin
  const isCustomerAdmin = user?.customer_role === 'admin'
  // super_admin and admin WITHOUT an assigned ERP skip user-facing module loading.
  // An ERP-scoped admin (has_erp=true) loads modules normally — they browse in multi-client mode.
  const isAdminWithoutERP = isAnyAdmin && !user?.has_erp
  const hasAllClients = !!enabledModules['all_clients']
  const isAdminRoute = location.pathname.startsWith('/admin')

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false)
      }
      if (erpSelectRef.current && !erpSelectRef.current.contains(e.target as Node)) {
        setErpSelectOpen(false)
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Load ERP list for super-admin impersonation selector
  useEffect(() => {
    if (isSuperAdmin && isAdminRoute) {
      api
        .getAdminERPs()
        .then((erps) => setErpList(erps))
        .catch(() => {})
    }
  }, [isSuperAdmin, isAdminRoute, location.pathname])

  // Load notifications from the backend and poll every 60 s (resets to page 0)
  const fetchNotifications = () => {
    api
      .getNotifications(0, NOTIF_PAGE)
      .then((data) => {
        setNotifications(data)
        setNotifOffset(0)
        setNotifHasMore(data.length === NOTIF_PAGE)
      })
      .catch(() => {})
  }

  const loadMoreNotifications = () => {
    if (notifLoadingMore || !notifHasMore) return
    const nextOffset = notifOffset + NOTIF_PAGE
    if (nextOffset >= NOTIF_MAX) {
      setNotifHasMore(false)
      return
    }
    setNotifLoadingMore(true)
    api
      .getNotifications(nextOffset, NOTIF_PAGE)
      .then((data) => {
        setNotifications((prev) => [...prev, ...data])
        setNotifOffset(nextOffset)
        setNotifHasMore(data.length === NOTIF_PAGE && nextOffset + NOTIF_PAGE < NOTIF_MAX)
      })
      .catch(() => {})
      .finally(() => setNotifLoadingMore(false))
  }

  useEffect(() => {
    fetchNotifications()
    const timer = setInterval(fetchNotifications, 60_000)
    return () => clearInterval(timer)
  }, [])

  // Restore impersonation display info from localStorage (survives page reload)
  const impersonatedId = api.getImpersonatedCustomer()
  const impersonatedName = impersonatedId
    ? (impersonatedClient?.customer_name ?? localStorage.getItem('impersonated_customer_name'))
    : null

  useEffect(() => {
    // Load ERP logo and supplier website URL
    api.getCustomerLogo().then((logo) => {
      if (logo?.content && logo?.mime_type) {
        setCustomerLogoUrl(`data:${logo.mime_type};base64,${logo.content}`)
      }
      if (logo?.website) setSupplierWebsite(logo.website)
    })
    if (isAdminWithoutERP) {
      setEnabledModules({})
      return
    }
    api
      .getEnabledModules()
      .then(setEnabledModules)
      .catch(() => {
        setEnabledModules({
          invoices: true,
          orders: true,
          shipments: true,
          tickets: true,
          catalog: true,
          gallery: true,
          revisions: true,
        })
      })
  }, [isAdminWithoutERP, setEnabledModules])

  // User-facing navigation: shown for regular users and ERP-scoped admins (has_erp=true).
  // Hidden for super_admin and admin without ERP assignment.
  const navigation = isAdminWithoutERP
    ? []
    : [
        { name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
        ...Object.entries(MODULE_NAV_MAP)
          .filter(([module]) => enabledModules[module])
          .map(([key, nav]) => ({ ...nav, name: t(`nav.${key}`) })),
        // 'Mon équipe' link: only visible to customer_role='admin' users
        ...(isCustomerAdmin ? [{ name: t('nav.myTeam'), href: '/my-team', icon: Users }] : []),
        // 'Mes paiements' always visible to logged-in customers
        { name: t('nav.myPayments'), href: '/payments', icon: CreditCard },
      ]

  const adminNavigation: AdminNavItem[] = [
    { name: t('nav.admin'), href: '/admin', icon: Shield, superAdminOnly: false },
    // ERPs management: super_admin only (ERP admins can view via /admin/erps but it's read-only)
    { name: t('nav.erps'), href: '/admin/erps', icon: Server, superAdminOnly: true },
    { name: t('nav.suppliers'), href: '/admin/customers', icon: Building2, superAdminOnly: false },
    { name: t('nav.users'), href: '/admin/users', icon: Users, superAdminOnly: false },
    { name: t('nav.logs'), href: '/admin/logs', icon: ScrollText, superAdminOnly: false },
    {
      name: t('nav.registrations'),
      href: '/admin/registrations',
      icon: UserPlus,
      superAdminOnly: false,
    },
    { name: t('nav.groups'), href: '/admin/groups', icon: UsersRound, superAdminOnly: false },
    {
      name: t('nav.pendingOrders'),
      href: '/admin/pending-orders',
      icon: ShoppingBag,
      superAdminOnly: false,
    },
    {
      name: t('nav.adminProposals'),
      href: '/admin/proposals',
      icon: FileSignature,
      superAdminOnly: false,
    },
    {
      name: 'Signatures libres',
      href: '/admin/document-signatures',
      icon: FileSignature,
      superAdminOnly: false,
    },
    { name: t('nav.adminProducts'), href: '/admin/products', icon: Package, superAdminOnly: false },
    { name: t('nav.adminUsage'), href: '/admin/usage', icon: BarChart2, superAdminOnly: false },
    { name: t('nav.adminBilling'), href: '/admin/billing', icon: CreditCard, superAdminOnly: true },
    {
      name: t('nav.adminMigration', 'Migration ERP'),
      href: '/admin/migration',
      icon: ArrowLeftRight,
      superAdminOnly: true,
    },
    // Configuration générale section
    { sectionLabel: t('nav.sectionConfig', 'Configuration générale'), superAdminOnly: false },
    { name: t('nav.adminRevisions'), href: '/admin/revisions', icon: Clock, superAdminOnly: false },
    {
      name: t('nav.adminNotificationRules', 'Règles de notification'),
      href: '/admin/notification-rules',
      icon: BellRing,
      superAdminOnly: false,
    },
    {
      name: t('nav.adminHelpTooltips', "Bulles d'aide"),
      href: '/admin/help-tooltips',
      icon: HelpCircle,
      superAdminOnly: false,
    },
    // ERP settings: accessible to ERP admins to manage their own ERP (description, logo, shop, modules, chatbot)
    ...(!isSuperAdmin && user?.erp_id
      ? [
          {
            name: t('nav.erpSettings', 'Paramètres ERP'),
            href: `/admin/erps/${user.erp_id}`,
            icon: Settings,
            superAdminOnly: false,
          },
        ]
      : []),
    // Supplier profile: editable company info + Dolibarr sync — ERP admins only
    {
      name: t('nav.adminProfile', 'Mon profil'),
      href: '/admin/profile',
      icon: Building2,
      superAdminOnly: false,
      erpAdminOnly: true,
    },
    // Abonnement: self-service subscription page, visible to ERP admins only (not super_admin who uses /admin/billing)
    {
      name: t('nav.tarifs'),
      href: '/tarifs',
      icon: CalendarCheck,
      superAdminOnly: false,
      erpAdminOnly: true,
    },
    // Mes crédits: credit balance and top-up, visible to ERP admins only
    {
      name: t('nav.mesCredits', 'Mes crédits'),
      href: '/mes-credits',
      icon: Wallet,
      superAdminOnly: false,
      erpAdminOnly: true,
    },
  ].filter(
    (item) => (!item.superAdminOnly || isSuperAdmin) && (!item.erpAdminOnly || !isSuperAdmin)
  )

  const unreadCount = notifications.filter((n) => !n.is_read).length

  const markAllRead = () => {
    api.markNotificationsRead().catch(() => {})
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
  }

  const handleNotifClick = (notif: AppNotification) => {
    if (!notif.is_read) {
      api.markNotificationsRead(notif.id).catch(() => {})
      setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n)))
    }
    setNotifOpen(false)
    if (notif.href) navigate(notif.href)
  }

  const handleNotifDelete = (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    api.deleteNotification(id).catch(() => {})
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  const formatNotifDate = (iso: string) => {
    // MySQL returns timestamps without timezone — append Z to treat as UTC
    const normalized = iso.endsWith('Z') || iso.includes('+') ? iso : iso.replace(' ', 'T') + 'Z'
    const diff = Math.floor((Date.now() - new Date(normalized).getTime()) / 1000)
    if (diff < 60) return "À l'instant"
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`
    return `Il y a ${Math.floor(diff / 86400)} j`
  }

  type NotifIconEntry = { icon: LucideIcon; color: string }
  const NOTIF_ICON_MAP: Record<string, NotifIconEntry> = {
    new_invoice: {
      icon: FileText,
      color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
    },
    invoice_updated: {
      icon: FileText,
      color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
    },
    payment_reminder: {
      icon: CreditCard,
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    },
    invoice_reminder: {
      icon: CreditCard,
      color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    },
    new_proposal: {
      icon: FileSignature,
      color: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    },
    esignature_reminder: {
      icon: FileSignature,
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    },
    new_document: {
      icon: LayoutGrid,
      color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    },
    registration: {
      icon: UserPlus,
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    },
    quota_exceeded: {
      icon: BarChart2,
      color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    },
    low_credits: {
      icon: Wallet,
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    },
    proposal_updated: {
      icon: FileSignature,
      color: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    },
    proposal_signed: {
      icon: CheckCircle2,
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    },
    proposal_refused: {
      icon: XCircle,
      color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    },
    document_signature_pending: {
      icon: FileSignature,
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    },
    document_signature_signed: {
      icon: CheckCircle2,
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    },
  }
  const NOTIF_ICON_DEFAULT: NotifIconEntry = {
    icon: Bell,
    color: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  }

  const handleLogout = () => {
    api.clearImpersonatedCustomer()
    api.clearAsERPAdmin()
    api.logout()
    navigate('/login')
  }

  const isActive = (path: string) => location.pathname === path

  // Highlight matched characters in a string
  const highlightMatch = (text: string, query: string) => {
    if (!query) return <span>{text}</span>
    const idx = text.toLowerCase().indexOf(query.toLowerCase())
    if (idx === -1) return <span>{text}</span>
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-yellow-100 text-yellow-900 font-semibold rounded px-0.5">
          {text.slice(idx, idx + query.length)}
        </mark>
        {text.slice(idx + query.length)}
      </>
    )
  }

  // Search with debounce
  const handleSearchChange = useCallback((q: string) => {
    setSearchQuery(q)
    setHighlightedIndex(-1)
    if (searchDebounce.current) clearTimeout(searchDebounce.current)
    if (q.length < 2) {
      setSearchResults([])
      setSearchLoading(false)
      setSearchOpen(q.length > 0) // keep open to show "type more" hint
      return
    }
    setSearchLoading(true)
    setSearchOpen(true)
    searchDebounce.current = setTimeout(async () => {
      try {
        const results = await api.searchClients(q)
        setSearchResults(results)
      } catch {
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 200)
  }, [])

  // Keyboard navigation in the autocomplete dropdown
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!searchOpen) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.min(i + 1, searchResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && searchResults[highlightedIndex]) {
        selectClient(searchResults[highlightedIndex])
      }
    } else if (e.key === 'Escape') {
      setSearchOpen(false)
      setHighlightedIndex(-1)
      searchInputRef.current?.blur()
    }
  }

  const selectClient = (client: ClientResult) => {
    api.setImpersonatedCustomer(client.erp_customer_id)
    localStorage.setItem('impersonated_customer_name', client.customer_name)
    setImpersonatedClient(client)
    setSearchQuery('')
    setSearchResults([])
    setSearchOpen(false)
    // Reload so all data fetches use the new X-As-Customer header
    window.location.reload()
  }

  const clearImpersonation = () => {
    api.clearImpersonatedCustomer()
    localStorage.removeItem('impersonated_customer_name')
    setImpersonatedClient(null)
    window.location.reload()
  }

  const impersonatedLabel = impersonatedId
    ? impersonatedName
      ? `${impersonatedName} (${impersonatedId})`
      : `#${impersonatedId}`
    : null

  // ERP admin impersonation helpers
  const asErpAdminId = api.getAsERPAdmin()
  const asErpAdminName = api.getAsERPAdminName()

  const selectERPAdmin = (erp: ERPOption) => {
    api.setAsERPAdmin(erp.id, erp.erp_name)
    setErpSelectOpen(false)
    window.location.reload()
  }

  const clearERPAdminImpersonation = () => {
    api.clearAsERPAdmin()
    window.location.reload()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar mobile */}
      <div
        className={`fixed inset-0 bg-gray-900 bg-opacity-50 z-40 lg:hidden transition-opacity ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200 transform transition-all duration-200 lg:translate-x-0 ${
          sidebarCollapsed ? 'w-16' : 'w-72'
        } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="h-full flex flex-col">
          {/* Logo + collapse button */}
          <div className="h-16 flex items-center justify-between px-3 border-b border-gray-200 gap-2">
            {!sidebarCollapsed &&
              (supplierWebsite ? (
                <a
                  href={supplierWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 flex items-center"
                  title={supplierWebsite}
                >
                  <img
                    src={customerLogoUrl ?? logoEspaceClient}
                    alt="Espace Client"
                    className="h-10 object-contain w-full"
                  />
                </a>
              ) : (
                <Link
                  to="/dashboard"
                  onClick={() => setSidebarOpen(false)}
                  className="flex-1 min-w-0 flex items-center"
                  title={t('nav.dashboard')}
                >
                  <img
                    src={customerLogoUrl ?? logoEspaceClient}
                    alt="Espace Client"
                    className="h-10 object-contain w-full"
                  />
                </Link>
              ))}
            {/* Close button — mobile only */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
            {/* Collapse toggle — desktop only */}
            <button
              onClick={toggleCollapsed}
              title={sidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
              className={`hidden lg:flex p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors ${sidebarCollapsed ? 'mx-auto' : 'flex-shrink-0'}`}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <ChevronLeft className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav
            className={`flex-1 py-4 space-y-1 overflow-y-auto ${sidebarCollapsed ? 'px-2' : 'px-4'}`}
          >
            {navigation.map((item) => {
              const Icon = item.icon
              const active = isActive(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  title={sidebarCollapsed ? item.name : undefined}
                  className={`flex items-center gap-3 py-3 rounded-lg transition-all ${
                    sidebarCollapsed ? 'justify-center px-2' : 'px-4'
                  } ${
                    active
                      ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span className="font-medium">{item.name}</span>}
                </Link>
              )
            })}

            {/* Admin section */}
            {isAnyAdmin && (
              <>
                <div className="pt-4 pb-2">
                  <div className="border-t border-gray-200"></div>
                  {!sidebarCollapsed && (
                    <p className="px-4 pt-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Administration
                    </p>
                  )}
                </div>
                {adminNavigation.map((item, idx) => {
                  if ('sectionLabel' in item) {
                    return (
                      <div key={`section-${idx}`} className="pt-3 pb-1">
                        <div className="border-t border-gray-200 dark:border-gray-700" />
                        {!sidebarCollapsed && (
                          <p className="px-4 pt-2 text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                            <Settings className="w-3 h-3" /> {item.sectionLabel}
                          </p>
                        )}
                      </div>
                    )
                  }
                  const Icon = item.icon
                  const active = isActive(item.href)
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      title={sidebarCollapsed ? item.name : undefined}
                      className={`flex items-center gap-3 py-3 rounded-lg transition-all ${
                        sidebarCollapsed ? 'justify-center px-2' : 'px-4'
                      } ${
                        active
                          ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-md'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {!sidebarCollapsed && <span className="font-medium">{item.name}</span>}
                    </Link>
                  )
                })}
              </>
            )}
          </nav>

          {/* User section */}
          <div className="p-3 border-t border-gray-200">
            {sidebarCollapsed ? (
              <div className="flex flex-col items-center gap-2">
                <div
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center"
                  title={user?.name ?? ''}
                >
                  <User className="w-5 h-5 text-white" />
                </div>
                <button
                  onClick={handleLogout}
                  title={t('common.logout')}
                  className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <>
                <div
                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 mb-3 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => navigate('/profile')}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.first_name} {user?.last_name?.toUpperCase()}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">{t('common.logout')}</span>
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-200 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-72'}`}>
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 flex-shrink-0"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-3 flex-shrink-0">
            <h2 className="hidden sm:block text-xl font-semibold text-gray-900">
              {navigation.find((item) => isActive(item.href))?.name ||
                adminNavigation.find(
                  (item): item is NavLinkItem => 'href' in item && isActive(item.href)
                )?.name ||
                t('nav.dashboard')}
            </h2>
          </div>

          {/* Client switcher (visible only for users with all_clients right) */}
          {hasAllClients && (
            <div className="flex-1 max-w-sm relative" ref={searchRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setSearchOpen(true)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={t('clientSwitcher.searchPlaceholder')}
                  autoComplete="off"
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50"
                />
                {/* Spinner while loading */}
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
                )}
              </div>

              {/* Autocomplete dropdown */}
              {searchOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
                  {searchQuery.length < 2 ? (
                    <p className="px-4 py-3 text-xs text-gray-400">
                      {t('clientSwitcher.typeMore')}
                    </p>
                  ) : searchLoading ? (
                    <p className="px-4 py-3 text-xs text-gray-400">
                      {t('clientSwitcher.searching')}
                    </p>
                  ) : searchResults.length === 0 ? (
                    <p className="px-4 py-3 text-xs text-gray-500">
                      {t('clientSwitcher.noResults')}
                    </p>
                  ) : (
                    searchResults.map((client, idx) => (
                      <button
                        key={client.id}
                        onClick={() => selectClient(client)}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                        className={`w-full text-left px-4 py-3 flex items-start gap-2 border-b border-gray-100 last:border-0 transition-colors ${
                          idx === highlightedIndex ? 'bg-primary-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <UserCheck className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {highlightMatch(client.customer_name, searchQuery)}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {highlightMatch(client.erp_customer_id, searchQuery)}
                            {client.city ? ` · ${client.city}` : ''}
                          </p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Super-admin ERP impersonation selector (admin routes only) */}
          {isSuperAdmin && isAdminRoute && (
            <div className="relative flex-shrink-0" ref={erpSelectRef}>
              <button
                onClick={() => setErpSelectOpen((o) => !o)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                  asErpAdminId
                    ? 'border-violet-400 bg-violet-50 text-violet-800'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {asErpAdminId ? asErpAdminName : t('admin.erpImpersonation.select')}
                </span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {erpSelectOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-48 max-h-64 overflow-y-auto">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
                    {t('admin.erpImpersonation.title')}
                  </div>
                  {asErpAdminId && (
                    <button
                      onClick={clearERPAdminImpersonation}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium border-b border-gray-100"
                    >
                      {t('admin.erpImpersonation.viewAll')}
                    </button>
                  )}
                  {erpList.map((erp) => (
                    <button
                      key={erp.id}
                      onClick={() => selectERPAdmin(erp)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-violet-50 transition-colors ${
                        asErpAdminId === String(erp.id)
                          ? 'bg-violet-50 text-violet-800 font-medium'
                          : 'text-gray-700'
                      }`}
                    >
                      {erp.erp_name}
                    </button>
                  ))}
                  {erpList.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-400 italic">
                      {t('common.noResults')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Notification bell */}
            <div className="relative flex-shrink-0" ref={notifRef}>
              <button
                onClick={() => setNotifOpen((o) => !o)}
                title="Notifications"
                className="relative p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {/* Placeholder — remplacer par une image <img src="..." /> */}
                <Bell className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute top-full right-0 mt-2 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50 flex flex-col max-h-[520px]">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                        {t('notifications.title')}
                        {unreadCount > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">
                            {unreadCount}
                          </span>
                        )}
                      </h3>
                      <button
                        onClick={() => setNotifHelpOpen((o) => !o)}
                        title={t('notifications.helpTitle')}
                        className={`p-0.5 rounded-full transition-colors ${notifHelpOpen ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                      >
                        <Info size={14} />
                      </button>
                    </div>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium transition-colors"
                      >
                        {t('notifications.markAllRead')}
                      </button>
                    )}
                  </div>

                  {/* Help bubble — types of notifications per role */}
                  {notifHelpOpen && (
                    <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
                      <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2">
                        {t('notifications.helpTitle')}
                      </p>
                      <ul className="space-y-1">
                        {(isSuperAdmin
                          ? (t('notifications.helpTypesSuperAdmin', {
                              returnObjects: true,
                            }) as string[])
                          : isAnyAdmin
                            ? (t('notifications.helpTypesAdmin', {
                                returnObjects: true,
                              }) as string[])
                            : (t('notifications.helpTypesUser', {
                                returnObjects: true,
                              }) as string[])
                        ).map((item: string, i: number) => (
                          <li
                            key={i}
                            className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400"
                          >
                            <span className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* List */}
                  <div
                    ref={notifListRef}
                    className="overflow-y-auto flex-1"
                    onScroll={(e) => {
                      const el = e.currentTarget
                      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                        loadMoreNotifications()
                      }
                    }}
                  >
                    {notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                        <Bell className="w-8 h-8 mb-2 opacity-30" />
                        <p className="text-sm">Aucune notification</p>
                      </div>
                    ) : (
                      <>
                        {notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className={`group flex items-start gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${!notif.is_read ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''}`}
                            onClick={() => handleNotifClick(notif)}
                          >
                            {(() => {
                              const { icon: NIcon, color } =
                                NOTIF_ICON_MAP[notif.type] ?? NOTIF_ICON_DEFAULT
                              return (
                                <span
                                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${color}`}
                                >
                                  <NIcon size={15} />
                                </span>
                              )
                            })()}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p
                                  className={`text-sm font-medium truncate ${notif.is_read ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}
                                >
                                  {notif.title}
                                </p>
                                {!notif.is_read && (
                                  <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                                )}
                              </div>
                              {notif.customer_name && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                  {notif.customer_name}
                                </p>
                              )}
                              <p className="text-[11px] text-gray-400 mt-1">
                                {formatNotifDate(notif.sent_at)}
                              </p>
                            </div>
                            <button
                              onClick={(e) => handleNotifDelete(e, notif.id)}
                              className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all"
                              title="Supprimer"
                            >
                              <X size={13} />
                            </button>
                          </div>
                        ))}
                        {notifLoadingMore && (
                          <div className="flex justify-center py-3">
                            <RefreshCw size={14} className="animate-spin text-gray-400" />
                          </div>
                        )}
                        {!notifHasMore && notifications.length > 0 && (
                          <p className="text-center text-xs text-gray-300 dark:text-gray-600 py-2">
                            —
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              title={t('darkMode.toggle')}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {isDark ? (
                <Sun className="w-4 h-4 text-amber-400" />
              ) : (
                <Moon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              )}
            </button>
            {/* Sélecteur de langue */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => i18n.changeLanguage('fr')}
                className={`px-2 py-1 text-xs font-semibold rounded-md transition-all ${
                  i18n.language === 'fr' || i18n.language?.startsWith('fr')
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                FR
              </button>
              <button
                onClick={() => i18n.changeLanguage('en')}
                className={`px-2 py-1 text-xs font-semibold rounded-md transition-all ${
                  i18n.language === 'en' || i18n.language?.startsWith('en')
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                EN
              </button>
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>
        </header>

        {/* Client impersonation banner */}
        {impersonatedId && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800">
              <UserCheck className="w-4 h-4" />
              <span className="text-sm font-medium">
                {t('clientSwitcher.viewingAs')} {impersonatedLabel}
              </span>
            </div>
            <button
              onClick={clearImpersonation}
              className="text-xs text-amber-700 hover:text-amber-900 font-medium underline"
            >
              {t('clientSwitcher.exitClient')}
            </button>
          </div>
        )}

        {/* ERP admin impersonation banner */}
        {asErpAdminId && isAdminRoute && (
          <div className="bg-violet-50 border-b border-violet-200 px-6 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-violet-800">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">
                {t('admin.erpImpersonation.banner', { name: asErpAdminName })}
              </span>
            </div>
            <button
              onClick={clearERPAdminImpersonation}
              className="text-xs text-violet-700 hover:text-violet-900 font-medium underline"
            >
              {t('admin.erpImpersonation.exit')}
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
